import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { TextFieldClasses } from '@mui/material/TextField/textFieldClasses';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { useUserConfig } from '../../../../context/UserConfigContext';
import { ExerciseOptionType } from '../../../../shared/constants';
import { getTimeValueFromOptionValue } from '../../../../utils/datetime/date-time-utils';
import AppTimeSetterField from '../../wheels/app-time-setter-field/AppTimeSetterField';
import AppStandardTextField from '../app-standart-text-field/AppStandardTextField';
import { useStyles } from './AppOptionTextField.styles';

type CustomTextFieldClasses = Partial<TextFieldClasses> & {
  number?: string;
  time?: string;
};

interface AppOptionTextFieldProps {
  type: ExerciseOptionType;
  name?: string;
  placeholder?: string | number;
  endAdornment?: string;
  classes?: CustomTextFieldClasses;
  onPlayClick?: () => void;
  onChange: (e: number) => void;
  playing?: boolean;
  fullWidth?: boolean;
  viewMode?: boolean;
  value: number;
  error?: boolean;
  helperText?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export default function AppOptionTextField(props: AppOptionTextFieldProps) {
  const {
    type,
    name,
    classes: customClasses = {},
    endAdornment,
    placeholder,
    onPlayClick,
    playing,
    onChange,
    fullWidth,
    viewMode,
    value,
    error,
    helperText,
    onFocus,
    onBlur,
  } = props;
  const { number: numberClass, time: timeClass, ...classes } = customClasses;
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes: optionClasses } = useStyles(theme);

  const spanRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);
  const { options } = useUserConfig();
  const showExerciseUnits = options.showExerciseUnits;
  useEffect(() => {
    if (spanRef.current) {
      setTextWidth(spanRef.current.offsetWidth);
    }
  }, [value, viewMode]);

  const isNumber = !Number.isNaN(+('' + value));

  switch (type) {
    case ExerciseOptionType.integer:
    case ExerciseOptionType.fractional: {
      return (
        <Box className={optionClasses.fieldContainer}>
          <Box ref={spanRef} className={optionClasses.fakeInput}>
            {isNumber ? value : placeholder}
          </Box>
          {viewMode && (
            <Box className={optionClasses.viewModeField}>
              {value ? (
                <>
                  {' '}
                  <Typography variant="text-md">{value}</Typography>
                  {showExerciseUnits && (
                    <Typography fontSize="10px" marginBottom="-4px">
                      {endAdornment}
                    </Typography>
                  )}
                </>
              ) : (
                '-'
              )}
            </Box>
          )}
          {!viewMode && (
            <AppStandardTextField
              placeholder={
                Number.isInteger(placeholder)
                  ? `${placeholder?.toString()}`
                  : placeholder?.toString()
              }
              type="number"
              inputMode="decimal"
              inputProps={{
                pattern: '^\\d*(\\.\\d+)?$',
              }}
              selectOnClick={true}
              InputProps={{
                endAdornment:
                  textWidth > 0 && showExerciseUnits ? (
                    <Box
                      className={clsx(
                        optionClasses.unitView,
                        !isNumber && optionClasses.programValue,
                      )}
                      left={`${textWidth}px`}>
                      {endAdornment}
                    </Box>
                  ) : null,
              }}
              onChange={(e) => {
                onChange(e.target.value ? parseFloat(e.target.value) : null);
              }}
              value={value}
              name={name}
              className={numberClass}
              classes={classes}
              error={error}
              helperText={helperText}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          )}
        </Box>
      );
    }
    case ExerciseOptionType.time: {
      const valueString = getTimeValueFromOptionValue(value, t);

      if (viewMode)
        return (
          <Box className={optionClasses.viewModeField}>
            {valueString ? `${valueString}${endAdornment}` : '-'}
          </Box>
        );

      return (
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          className={optionClasses.timeSetterFieldContainer}>
          {/*{onPlayClick && (*/}
          {/*  <PlayActionButton*/}
          {/*    playing={playing}*/}
          {/*    disabled={playing}*/}
          {/*    onClick={onPlayClick}*/}
          {/*  />*/}
          {/*)}*/}

          <AppTimeSetterField
            value={value}
            placeholder={placeholder || null}
            fullWidth={fullWidth}
            onChange={(value) => onChange(value)}
            classes={classes}
            error={error}
            helperText={helperText}
          />
        </Box>
      );
    }
    default:
      return null;
  }
}
